import { keysOf, removeNullAndUndefined } from '@upright/common/utils';
import { isNil } from 'lodash';

// See https://developers.hubspot.com/docs/reference/api/marketing/forms/v3-legacy
// for endpoint documentation

const portalId = '6314920';

interface PageContext {
  pageUri: string;
  pageName: string;
}

type LegalConsentOptions = {
  consent: {
    consentToProcess: boolean;
    text: string;
    communications: Array<{
      value: boolean;
      subscriptionTypeId: number;
      text: string;
    }>;
  };
};

type HubspotAPIRequest = {
  portalId: string;
  formGuid: string;
  fields: Array<{
    objectTypeId?: string; // Seems to work without
    name: string | number;
    value: string | number;
  }>;
  context?: PageContext;
  legalConsentOptions?: LegalConsentOptions;
  hubspotutk?: string;
};

export function getHubspotFormSubmitParams(
  formGuid: string,
  fields: Record<string | number, string | number | boolean | undefined | null>,
  metadata?: {
    authToken?: string;
    context?: PageContext;
    agreedToComms?: boolean;
    hubspotutk?: string;
  },
) {
  const fieldsConverted = keysOf(fields)
    .map(key => ({
      name: key,
      value: fields[key],
    }))
    // Hubspot API doesn't like undefined or null values, so let's drop them.
    .filter(d => !isNil(d.value))
    // Hubspot "boolean" is string that represents a checkbox that is either checked "Yes" or not checked "No",
    // so we'll convert all our booleans to that format here.
    .map(d => {
      if (typeof d.value === 'boolean') {
        return { ...d, value: d.value ? 'Yes' : 'No' };
      } else {
        return d as typeof d & { value: Exclude<typeof d.value, boolean> };
      }
    });

  const legalConsentOptions: LegalConsentOptions = {
    consent: {
      consentToProcess: true,
      text: 'I agree to allow Upright Project to store and process my personal data.',
      communications: metadata?.agreedToComms
        ? [
            {
              value: true,
              // Note: this seems to be the ID in the form we have,
              // not sure of the purpose of this
              subscriptionTypeId: 7208089,
              text: 'I agree to receive other communications from The Upright Project.',
            },
          ]
        : [],
    },
  };

  const request: HubspotAPIRequest = removeNullAndUndefined({
    portalId: portalId,
    formGuid,
    fields: fieldsConverted,
    context: metadata?.context,
    legalConsentOptions,
    hubspotutk: metadata?.hubspotutk,
  });

  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${request.formGuid}`;

  const auth: { Authorization: string } | object = metadata?.authToken
    ? { Authorization: `Bearer ${metadata.authToken}` }
    : {};

  const init = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...auth,
    },
    body: JSON.stringify(request),
  };

  return { url, init };
}
